import { useCallback, useEffect, useRef, useState } from 'react'

import { TextField } from '@/components/TextField'
import { cn } from '@/lib/utils'

export interface AutoCompleteFieldProps<T> {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  onSelect?: (option: T) => void
  options: T[]
  getOptionLabel: (option: T) => string
  loading?: boolean
  disabled?: boolean
  error?: boolean
  supportingText?: string
  placeholder?: string
  className?: string
}

export function AutoCompleteField<T>({
  id,
  label,
  value,
  onChange,
  onSelect,
  options,
  getOptionLabel,
  loading = false,
  disabled = false,
  error = false,
  supportingText,
  placeholder,
  className,
}: AutoCompleteFieldProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)

  const selectOption = useCallback(
    (option: T) => {
      const label = getOptionLabel(option)
      onChange(label)
      onSelect?.(option)
      setIsOpen(false)
      setActiveIndex(-1)
    },
    [getOptionLabel, onChange, onSelect]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen || !options.length) {
        if (e.key === 'Escape') setIsOpen(false)
        return
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex(i => (i < options.length - 1 ? i + 1 : 0))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex(i => (i <= 0 ? options.length - 1 : i - 1))
      } else if (e.key === 'Enter' && options.length > 0) {
        e.preventDefault()
        selectOption(options[activeIndex >= 0 ? activeIndex : 0])
      } else if (e.key === 'Escape') {
        setIsOpen(false)
        setActiveIndex(-1)
      }
    },
    [isOpen, options, activeIndex, selectOption]
  )

  const handleFocus = useCallback(() => {
    setIsOpen(true)
  }, [])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value)
      setActiveIndex(-1)
      setIsOpen(true)
    },
    [onChange]
  )

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const handler = (e: MouseEvent) => {
      if (el.contains(e.target as Node)) return
      setIsOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const showList = isOpen && (options.length > 0 || loading)

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <TextField
        id={id}
        label={label}
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        error={error}
        supportingText={supportingText}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete='off'
        aria-autocomplete='list'
        aria-expanded={isOpen}
        aria-controls={`${id}-listbox`}
        aria-activedescendant={activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined}
      />
      {showList && (
        <ul
          id={`${id}-listbox`}
          role='listbox'
          className='border-border bg-popover text-popover-foreground absolute start-0 top-full z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border shadow-md'
        >
          {loading && options.length === 0 ? (
            <li className='text-muted-foreground px-3 py-2 text-sm'>טוען...</li>
          ) : options.length === 0 ? (
            <li className='text-muted-foreground px-3 py-2 text-sm'>לא נמצאו תוצאות</li>
          ) : (
            options.map((option, i) => {
              const label = getOptionLabel(option)
              return (
                <li
                  key={`${label}-${i}`}
                  id={`${id}-option-${i}`}
                  role='option'
                  aria-selected={i === activeIndex}
                  className={cn(
                    'cursor-pointer px-3 py-2 text-sm transition-colors',
                    i === activeIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-muted',
                    value === label && 'bg-muted'
                  )}
                  onMouseEnter={() => setActiveIndex(i)}
                  onMouseDown={e => {
                    e.preventDefault()
                    selectOption(option)
                  }}
                >
                  {label}
                </li>
              )
            })
          )}
        </ul>
      )}
    </div>
  )
}
